import { CONFIG, promoteCountForGroup, relegateCountForGroup } from '@h2h/shared';
import type { Zone } from './types';

/** Which competition zone a rank sits in for a group of `size` players. The
 *  promotion/relegation bands scale with the group size (see the engine's
 *  `movementCountsForGroup`), so a small or partial group shows the same fraction
 *  of movers the server will actually settle — never a flat top/bottom 5. */
export function zoneForRank(rank: number, size: number): Zone {
  const promote = promoteCountForGroup(size);
  const relegate = relegateCountForGroup(size);
  if (promote > 0 && rank <= promote) return 'promotion';
  if (relegate > 0 && rank > size - relegate) return 'relegation';
  return 'middle';
}

/** Human label for a player's projected position in their division. */
export function zoneLabel(zone: Zone): string {
  switch (zone) {
    case 'promotion':
      return 'In the promotion zone';
    case 'relegation':
      return 'In the relegation zone';
    default:
      return 'Holding mid-table';
  }
}

/** Tier before a settlement move, given the tier landed on and the movement. */
export function priorTier(currentTier: number, movement: 'PROMOTED' | 'STAYED' | 'RELEGATED'): number {
  if (movement === 'PROMOTED') return Math.max(0, currentTier - 1);
  if (movement === 'RELEGATED') return Math.min(CONFIG.TIERS.length - 1, currentTier + 1);
  return currentTier;
}

/** Group number (1-based) parsed from the engine's deterministic groupId `t{tier}-g{idx}`. */
export function groupNumber(groupId: string): number {
  const idx = Number(groupId.split('-g')[1] ?? 0);
  return Number.isFinite(idx) ? idx + 1 : 1;
}
